import { useMemo } from 'react';
import { uniqBy } from 'lodash-es';
import { ChangeLogItemProps } from '@lp/features/RoutePlanner/components/LogisticsChangeLog/consts';
import {
  isAccommodationLegModifiedLog,
  isAddRemoveAccommodationLegLogs,
  isAddRemoveRouteLegLogs,
  isAddRemoveSabreLogs,
  isGroundTripAssignedLogs,
  isGroundTripReassignedLogs,
  isModifiedRouteLegLogs,
  isRouteLogWithLegList,
  isSuggestedRoutesAppliedLogs,
  unwrapModifiedEventLog,
} from '@lp/features/RoutePlanner/components/LogisticsChangeLog/utils';
import { LogisticsChangeLogType, LogisticsResourceType } from '@/api/apis/route';
import { ChangeLogRouteLocationsList } from './ChangeLogRouteLocationsList';
import { ChangeLogHeader } from './ChangeLogHeader';
import { CreatedByUser } from './CreatedByUser';
import { ChangeLogResourcesList } from './ChangeLogResourcesList';
import { ChangeLogLocations } from './ChangeLogLocations';
import { ModifiedChangeLog } from './ModifiedChangeLog';
import { GroundTripLog } from './GroundTripLog';
import { SuggestedRoutesLog } from './SuggestedRoutesLog';

export const ChangeLogItem = ({ log }: ChangeLogItemProps) => {
  const isAddRemoveRouteLegLog = isAddRemoveRouteLegLogs(log);
  const isAddRemoveAccommodationLegLog = isAddRemoveAccommodationLegLogs(log);
  const isAddRemoveSabreLegsLog = isAddRemoveSabreLogs(log);
  const isModifiedRouteLegsLog = isModifiedRouteLegLogs(log);
  const isModifiedAccommodationLog = isAccommodationLegModifiedLog(log);
  const isLogWithLegs = isRouteLogWithLegList(log);
  const isResourceRemovedLog = log.$type === LogisticsChangeLogType.LogisticsResourceRemovedLog;
  const isResourceSwappedLog = log.$type === LogisticsChangeLogType.LogisticsResourceSwappedLog;
  const isSuggestedRoutesAppliedLog = isSuggestedRoutesAppliedLogs(log);
  const isGroundTripAssignedLog = isGroundTripAssignedLogs(log);
  const isGroundTripReassignedLog = isGroundTripReassignedLogs(log);

  const isModifiedChangeLog = isModifiedAccommodationLog || isModifiedRouteLegsLog;

  const resources = useMemo(() => {
    if (isAddRemoveSabreLegsLog) {
      return uniqBy(
        log.legs.flatMap((leg) => leg.resources),
        'resourceId',
      );
    }

    if (isAddRemoveRouteLegLog || isAddRemoveAccommodationLegLog || isGroundTripAssignedLog) {
      return log.resources;
    }

    if (isGroundTripReassignedLog) {
      return log.leg.resources;
    }

    if (isModifiedChangeLog) {
      return unwrapModifiedEventLog<LogisticsResourceType[]>(log.leg.resources);
    }

    return [];
  }, [
    log,
    isAddRemoveRouteLegLog,
    isAddRemoveAccommodationLegLog,
    isAddRemoveSabreLegsLog,
    isModifiedChangeLog,
    isGroundTripAssignedLog,
    isGroundTripReassignedLog,
  ]);

  const isModifiedChangeLogView = isModifiedChangeLog || isGroundTripReassignedLog;

  const isLogWithResources =
    isAddRemoveRouteLegLog ||
    isAddRemoveAccommodationLegLog ||
    isAddRemoveSabreLegsLog ||
    isModifiedChangeLog ||
    isGroundTripReassignedLog ||
    isGroundTripAssignedLog;

  return (
    <div id={log.id} className="flex flex-col py-2 px-4 gap-2 rounded-lg bg-base-80">
      <ChangeLogHeader log={log} />
      {!isLogWithLegs && !isSuggestedRoutesAppliedLog && <ChangeLogLocations log={log} />}
      {isLogWithResources && (
        <ChangeLogResourcesList
          logType={log.$type}
          resources={resources}
          hasScs={isAddRemoveRouteLegLog && log.hasScs}
        />
      )}
      {isLogWithLegs && !!log.legs.length && <ChangeLogRouteLocationsList logType={log.$type} legs={log.legs} />}
      {isResourceSwappedLog && !!log.affectedLegs.length && (
        <ChangeLogRouteLocationsList logType={log.$type} legs={log.affectedLegs} />
      )}
      {isSuggestedRoutesAppliedLog && <SuggestedRoutesLog log={log} />}
      {isResourceRemovedLog && (
        <>
          {!!log.affectedLegs.length && (
            <ChangeLogRouteLocationsList logType={log.$type} legs={log.affectedLegs} title="Affected legs" />
          )}
          {!!log.removedLegs.length && (
            <ChangeLogRouteLocationsList logType={log.$type} legs={log.removedLegs} title="Removed legs" />
          )}
        </>
      )}
      {isModifiedChangeLogView && <ModifiedChangeLog log={log} />}
      {isGroundTripAssignedLog && <GroundTripLog log={log} />}
      <CreatedByUser user={log.createdBy} timestamp={log.createdAt} />
    </div>
  );
};
